import { Button, CircularProgress, IconButton, Select } from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { editOrganization } from '~/api/organizations';
import type { OrganizationDetail } from '~/common/types';
import Input from '~/components2/forms/Input';
import Notify from '~/components2/Notify';
import rootStore from '~/store';

const domainRegex = /^(?!-)(?!.*-$)(?!.*\.\.)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,63}$/;

export default function AllowedDomainsInput() {
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [defaultRole, setDefaultRole] = useState('contributor');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [isValid, setIsValid] = useState(true);

  const { currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;

  useEffect(() => {
    if (!currentOrganizationDetail.isLoading) {
      setAllowedDomains(currentOrganizationDetail.allowedDomains);
      setDefaultRole(currentOrganizationDetail.defaultRole);
    }
  }, [currentOrganizationDetail]);

  const validateDomains = (domains: string[]) => {
    const trimmedDomains = domains.map((domain) => domain.trim().toLowerCase());

    if (trimmedDomains.length === 0) {
      setIsValid(false);
      return;
    }

    // only one domain is allowed for now i.e don't allow comma separated domains
    if (trimmedDomains.length > 1 || trimmedDomains[0].includes(',')) {
      setIsValid(false);
      return;
    }

    if (!trimmedDomains.every((domain) => domainRegex.test(domain))) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
  };

  const handleSaveAllowedDomains = async () => {
    const trimmedDomains = allowedDomains.filter((contact) => contact.trim().length > 0);

    await editOrganization((currentOrganizationDetail as OrganizationDetail).id, {
      allowedDomains: trimmedDomains,
      defaultRole,
    });

    Notify.info({
      title: 'Domain Successfully Updated',
      message: 'Domain successfully updated',
    });

    setMode('view');
  };

  const handleRemoveAllowedDomains = async () => {
    setAllowedDomains([]);
    await editOrganization((currentOrganizationDetail as OrganizationDetail).id, {
      allowedDomains: [],
    });

    Notify.info({
      title: 'Domain Successfully Removed',
      message: 'Domain successfully removed',
    });

    setMode('view');
  };

  const onChange = (value: string) => {
    const domains = value.split(',').map((contact) => contact.trim());
    setAllowedDomains(domains);
    validateDomains(domains);
  };

  const getLabelForRole = (role: string) => {
    if (!currentOrganizationDetail.isLoading) {
      const theRole = currentOrganizationDetail.roles.find((r) => r.roleId === role);
      if (theRole) {
        return theRole.name;
      } else {
        return 'Contact Support'; // this should never occur
      }
    }
    return '';
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const contents = useMemo(() => {
    if (!currentOrganizationDetail.isLoading) {
      if (mode === 'view') {
        if (allowedDomains.length === 0) {
          return (
            <Button
              variant="secondary"
              data-testid="org-domains-add-button"
              onClick={() => {
                validateDomains(allowedDomains);
                setMode('edit');
              }}
            >
              Add Domain
            </Button>
          );
        } else {
          return (
            <div className="flex items-center space-x-2">
              <div className="flex items-center ">
                <div>@{allowedDomains.join(', ')}</div>
                <div className="ml-2 rounded-sm bg-secondary-light p-1 px-3">{getLabelForRole(defaultRole)}</div>
              </div>

              <IconButton
                variant="secondary"
                icon="gear"
                onClick={() => setMode('edit')}
                aria-label="Edit allowed domains"
                data-testid="org-domains-edit-button"
              />
            </div>
          );
        }
      } else {
        // in edit mode
        return (
          <div className="space-y-2">
            <div className="flex space-x-2">
              <span className="w-[200px]">
                <Input
                  autoFocus
                  label="Domain"
                  placeholder="acme-engineers.com"
                  value={allowedDomains.join(',')}
                  onChange={onChange}
                  data-testid="org-domains-input"
                />
              </span>
              <Select
                label="Default Role"
                value={defaultRole}
                onValueChange={(r) => r && setDefaultRole(r)}
                options={currentOrganizationDetail.roles.map((r) => ({
                  value: r.roleId,
                  label: r.name,
                }))}
                tooltipText={
                  <div>
                    The default role assigned to users who auto-join.{' '}
                    <Link
                      to="/orgSettings/$tabId"
                      params={{ tabId: 'roles' }}
                      className="underline"
                      data-testid="org-domains-view-roles-link"
                    >
                      View Roles
                    </Link>
                    .
                  </div>
                }
                data-testid="org-domains-default-role-select"
              />
            </div>

            <div className="space-x-2">
              <Button variant="secondary" onClick={handleRemoveAllowedDomains} data-testid="org-domains-delete-button">
                Delete
              </Button>
              <Button disabled={!isValid} onClick={handleSaveAllowedDomains} data-testid="org-domains-save-button">
                Save
              </Button>
            </div>
            {!isValid && <div className="mt-1 text-xs text-red-500">Please enter a valid email domain</div>}
          </div>
        );
      }
    }
    return null;
  }, [currentOrganizationDetail, mode, allowedDomains, defaultRole]);

  if (currentOrganizationDetail.isLoading) {
    return <CircularProgress indeterminate />;
  }

  return (
    <div className="flex w-2/3 flex-col">
      <div className="text-sm font-bold">Enable auto-join for your domain</div>
      <div className="flex w-2/3 flex-col space-y-1 pb-2">
        <div className="text-sm">
          When enabled, any user with an email from the domain provided below can join your organization as a member.
          Organization owners will be notified via email when a new user joins.
        </div>
        <div className="text-xs italic">
          (e.g. setting the value to civilgrid.com will allow alex@civilgrid.com to automatically join)
        </div>
      </div>

      <div>{contents}</div>
    </div>
  );
}
